import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, Shield, Award, Eye, Users, Trophy, Calendar, Radio,
  ChevronDown, ChevronRight, Search, Bell, FileSpreadsheet, 
  UserPlus, Settings, Play, Target, BarChart3, Globe, HelpCircle,
  CheckCircle, XCircle, ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/f617a5a9-dda7-40dc-bbb9-de762658adaf/images/5d32d876981551b4c98ceec76bfdad1e26bc99093c09f2a5f2242b603b114d79.png";

export default function GuidePage() {
  const [activeSection, setActiveSection] = useState('users');

  const userTypes = [
    {
      id: 'super_admin',
      title: 'Super Admin',
      subtitle: 'Platform Owner',
      icon: Shield,
      color: 'bg-purple-500',
      badge: 'Full Access',
      badgeColor: 'bg-purple-100 text-purple-700',
      description: 'Complete control over the entire CricORama platform. Can manage all leagues, users, and settings.',
      howToGet: 'Pre-configured account. Contact platform owner.',
      features: [
        { name: 'View all leagues', allowed: true },
        { name: 'Create/Edit/Delete any league', allowed: true },
        { name: 'Manage all teams & players', allowed: true },
        { name: 'Create League Admins', allowed: true },
        { name: 'Assign leagues to admins', allowed: true },
        { name: 'Create Scorer accounts', allowed: true },
        { name: 'View & manage all users', allowed: true },
        { name: 'Bulk data import/export', allowed: true },
      ]
    },
    {
      id: 'league_admin',
      title: 'League Admin',
      subtitle: 'Tournament Organizer',
      icon: Trophy,
      color: 'bg-blue-500',
      badge: 'Own Leagues',
      badgeColor: 'bg-blue-100 text-blue-700',
      description: 'Manage your own cricket leagues, teams, players, and match schedules. Perfect for tournament organizers.',
      howToGet: 'Sign up and select "League Admin" account type.',
      features: [
        { name: 'Create new leagues (becomes owner)', allowed: true },
        { name: 'Edit/Delete own leagues', allowed: true },
        { name: 'Create teams & add players', allowed: true },
        { name: 'Schedule matches', allowed: true },
        { name: 'Assign scorers to matches', allowed: true },
        { name: 'Create Scorer accounts', allowed: true },
        { name: 'Bulk upload players (CSV)', allowed: true },
        { name: 'View other admins\' leagues', allowed: false },
        { name: 'Create other admins', allowed: false },
      ]
    },
    {
      id: 'scorer',
      title: 'Scorer',
      subtitle: 'Match Official',
      icon: Radio,
      color: 'bg-green-500',
      badge: 'Assigned Matches',
      badgeColor: 'bg-green-100 text-green-700',
      description: 'Score live matches ball-by-ball. Can only access matches assigned by admins.',
      howToGet: 'Scorer accounts are created by Admins only. Contact your League Admin.',
      features: [
        { name: 'View assigned matches', allowed: true },
        { name: 'Start matches & record toss', allowed: true },
        { name: 'Select playing XI', allowed: true },
        { name: 'Ball-by-ball scoring', allowed: true },
        { name: 'Record runs, wickets, extras', allowed: true },
        { name: 'Create match highlights', allowed: true },
        { name: 'Undo last ball', allowed: true },
        { name: 'Create leagues/teams', allowed: false },
        { name: 'Access admin dashboard', allowed: false },
      ]
    },
    {
      id: 'viewer',
      title: 'Fan / Viewer',
      subtitle: 'Public User',
      icon: Eye,
      color: 'bg-teal-500',
      badge: 'Read Only',
      badgeColor: 'bg-teal-100 text-teal-700',
      description: 'Browse leagues, watch live scores, view statistics, and follow your favorite teams and players.',
      howToGet: 'Sign up and select "Fan / Viewer" account type.',
      features: [
        { name: 'Browse all leagues & teams', allowed: true },
        { name: 'View player profiles & stats', allowed: true },
        { name: 'Watch live match scores', allowed: true },
        { name: 'View standings & leaderboards', allowed: true },
        { name: 'Search players, teams, matches', allowed: true },
        { name: 'Watch live streams', allowed: true },
        { name: 'Score matches', allowed: false },
        { name: 'Create/Edit any data', allowed: false },
      ]
    }
  ];

  const quickLinks = [
    { name: 'Live Matches', url: '/live', icon: Radio, description: 'Watch ongoing matches' },
    { name: 'All Leagues', url: '/leagues', icon: Trophy, description: 'Browse leagues' },
    { name: 'Player Stats', url: '/stats', icon: BarChart3, description: 'Leaderboards & rankings' },
    { name: 'Search', url: '/', icon: Search, description: 'Find players, teams' },
    { name: 'Sign Up', url: '/signup', icon: UserPlus, description: 'Create an account' },
    { name: 'Login', url: '/login', icon: Users, description: 'Access your dashboard' },
  ];

  const adminLinks = [
    { name: 'Admin Dashboard', url: '/admin', role: 'Admin' },
    { name: 'Manage Leagues', url: '/admin/leagues', role: 'Admin' },
    { name: 'Manage Teams', url: '/admin/teams', role: 'Admin' },
    { name: 'Manage Players', url: '/admin/players', role: 'Admin' },
    { name: 'Schedule Matches', url: '/admin/matches', role: 'Admin' },
    { name: 'Manage Users', url: '/admin/manage-users', role: 'Admin' },
    { name: 'Bulk Data Import/Export', url: '/admin/bulk-data', role: 'Admin' },
    { name: 'Scorer Dashboard', url: '/scorer', role: 'Scorer' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back to Home</span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="CricORama" className="w-8 h-8" />
            <span className="font-bold text-slate-900">CricORama Guide</span>
          </div>
          <Link to="/login">
            <Button size="sm" className="bg-teal-600 hover:bg-teal-700">
              Login
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#2D5A5A] to-[#1E4545] text-white py-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <Badge className="bg-white/20 text-white border-none mb-4">
            <HelpCircle className="w-4 h-4 mr-1" />
            User Guide
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Welcome to CricORama
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            Your complete guide to managing cricket leagues, scoring matches, and following your favorite teams.
          </p>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-8 px-4 bg-white border-b">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Quick Links</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {quickLinks.map((link) => (
              <Link key={link.url} to={link.url}>
                <Card className="hover:shadow-md transition-all hover:-translate-y-0.5 h-full">
                  <CardContent className="p-4 text-center">
                    <link.icon className="w-6 h-6 mx-auto mb-2 text-teal-600" />
                    <p className="font-medium text-sm">{link.name}</p>
                    <p className="text-xs text-slate-500 mt-1">{link.description}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* User Types */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">User Types & Access Levels</h2>
            <p className="text-slate-600">CricORama supports four types of users with different capabilities</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {userTypes.map((user) => (
              <Card key={user.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <CardHeader className={`${user.color} text-white py-4`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                        <user.icon className="w-6 h-6" />
                      </div>
                      <div>
                        <CardTitle className="text-xl">{user.title}</CardTitle>
                        <p className="text-white/80 text-sm">{user.subtitle}</p>
                      </div>
                    </div>
                    <Badge className={user.badgeColor}>{user.badge}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-5">
                  <p className="text-slate-600 mb-4">{user.description}</p>
                  
                  <div className="bg-slate-50 rounded-lg p-3 mb-4">
                    <p className="text-sm">
                      <span className="font-medium text-slate-700">How to get this role: </span>
                      <span className="text-slate-600">{user.howToGet}</span>
                    </p>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700">Capabilities:</p>
                    <div className="grid grid-cols-1 gap-1">
                      {user.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          {feature.allowed ? (
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                          )}
                          <span className={feature.allowed ? 'text-slate-700' : 'text-slate-400'}>
                            {feature.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Important Notice for Scorers */}
      <section className="py-8 px-4 bg-amber-50">
        <div className="max-w-6xl mx-auto">
          <Card className="border-amber-200 bg-white">
            <CardContent className="p-6 flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Award className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-900 mb-2">
                  Want to be a Scorer?
                </h3>
                <p className="text-slate-600 mb-3">
                  Scorer accounts cannot be created through self-registration. They must be created by a League Admin or Super Admin. 
                  If you want to score matches for a league:
                </p>
                <ol className="list-decimal list-inside text-slate-600 space-y-1">
                  <li>Contact the League Admin who runs the tournament</li>
                  <li>Ask them to create a Scorer account for you</li>
                  <li>They will provide you with login credentials</li>
                  <li>Once logged in, you'll see your assigned matches at <code className="bg-slate-100 px-1 rounded">/scorer</code></li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* How-To Guides */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">How-To Guides</h2>
            <p className="text-slate-600">Step-by-step instructions for common tasks</p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="create-league" className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Trophy className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="font-medium">How to Create a League (Admin)</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-2 pb-4">
                <ol className="list-decimal list-inside space-y-2 text-slate-600">
                  <li>Login with your Admin account</li>
                  <li>Go to <Link to="/admin/leagues" className="text-teal-600 hover:underline">Admin Dashboard → Leagues</Link></li>
                  <li>Click <strong>"Create League"</strong> button</li>
                  <li>Fill in league details: Name, Season, Location, Start/End dates</li>
                  <li>Optionally upload a league logo</li>
                  <li>Click <strong>Save</strong></li>
                </ol>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="add-team" className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                    <Users className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="font-medium">How to Add Teams & Players (Admin)</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-2 pb-4">
                <div className="space-y-4 text-slate-600">
                  <div>
                    <p className="font-medium text-slate-700 mb-2">Adding a Team:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Go to <Link to="/admin/teams" className="text-teal-600 hover:underline">Admin → Teams</Link></li>
                      <li>Click <strong>"Add Team"</strong></li>
                      <li>Select the league, enter team name and short code</li>
                      <li>Click <strong>Save</strong></li>
                    </ol>
                  </div>
                  <div>
                    <p className="font-medium text-slate-700 mb-2">Adding Players:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Go to team detail page</li>
                      <li>Click <strong>"Add Player"</strong> for individual, or <strong>"Bulk Upload"</strong> for CSV</li>
                      <li>Fill in player details (name, phone, email, role)</li>
                      <li>Click <strong>Save</strong></li>
                    </ol>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="schedule-match" className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-purple-600" />
                  </div>
                  <span className="font-medium">How to Schedule a Match (Admin)</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-2 pb-4">
                <ol className="list-decimal list-inside space-y-2 text-slate-600">
                  <li>Go to <Link to="/admin/matches" className="text-teal-600 hover:underline">Admin → Matches</Link></li>
                  <li>Click <strong>"Schedule Match"</strong></li>
                  <li>Select League, Team A, Team B</li>
                  <li>Set Date, Time, Venue, and Overs</li>
                  <li>Assign a Scorer (optional)</li>
                  <li>Add YouTube Live URL for streaming (optional)</li>
                  <li>Click <strong>Save</strong></li>
                </ol>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="score-match" className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                    <Radio className="w-4 h-4 text-red-600" />
                  </div>
                  <span className="font-medium">How to Score a Match (Scorer)</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-2 pb-4">
                <div className="space-y-4 text-slate-600">
                  <div>
                    <p className="font-medium text-slate-700 mb-2">Starting the Match:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Login with Scorer credentials</li>
                      <li>Go to <Link to="/scorer" className="text-teal-600 hover:underline">/scorer</Link></li>
                      <li>Find your assigned match and click <strong>"Start Match"</strong></li>
                      <li>Record toss winner and decision (bat/bowl)</li>
                      <li>Select Playing XI for both teams</li>
                      <li>Select opening batsmen and opening bowler</li>
                    </ol>
                  </div>
                  <div>
                    <p className="font-medium text-slate-700 mb-2">During the Match:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Use run buttons (0-6) after each delivery</li>
                      <li>Click <strong>"Wicket"</strong> to record dismissals</li>
                      <li>Click <strong>"Extra"</strong> for wides, no-balls, byes</li>
                      <li>Use <strong>"Undo"</strong> to correct mistakes</li>
                      <li>Highlights auto-create on boundaries and wickets</li>
                    </ol>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="bulk-import" className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                    <FileSpreadsheet className="w-4 h-4 text-teal-600" />
                  </div>
                  <span className="font-medium">How to Bulk Import/Export Data (Admin)</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-2 pb-4">
                <div className="space-y-4 text-slate-600">
                  <div>
                    <p className="font-medium text-slate-700 mb-2">Export All Data:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Go to <Link to="/admin/bulk-data" className="text-teal-600 hover:underline">Admin → Bulk Data</Link></li>
                      <li>Click <strong>"Export to Excel"</strong></li>
                      <li>Download contains: Leagues, Teams, Players, Matches, Scorers</li>
                    </ol>
                  </div>
                  <div>
                    <p className="font-medium text-slate-700 mb-2">Import Data:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Click <strong>"Import from Excel"</strong></li>
                      <li>Download template first to see required format</li>
                      <li>Fill in your data following the template</li>
                      <li>Upload and review import results</li>
                    </ol>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Admin URLs Reference */}
      <section className="py-12 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Admin & Scorer URLs</h2>
            <p className="text-slate-600">Quick reference for authenticated pages</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {adminLinks.map((link) => (
              <Link key={link.url} to={link.url}>
                <Card className="hover:shadow-md transition-all h-full">
                  <CardContent className="p-4">
                    <Badge className="mb-2 text-xs" variant="outline">{link.role}</Badge>
                    <p className="font-medium text-slate-900">{link.name}</p>
                    <code className="text-xs text-teal-600 bg-teal-50 px-1 rounded">{link.url}</code>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-[#1E4545] text-white">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="CricORama" className="w-8 h-8" />
            <span className="font-semibold">CricORama</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-teal-200">
            <Link to="/" className="hover:text-white">Home</Link>
            <Link to="/leagues" className="hover:text-white">Leagues</Link>
            <Link to="/live" className="hover:text-white">Live</Link>
            <Link to="/login" className="hover:text-white">Login</Link>
          </div>
          <p className="text-teal-300 text-sm">© 2026 CricORama</p>
        </div>
      </footer>
    </div>
  );
}
